<template>
  <f7-popup class="popup-registrar" :opened="opened" @popup:opened="abrir" @popup:closed="$emit('cerrar')">
    <f7-page>
      <f7-navbar :title="$t('registrarVacaciones.titulo')">
        <f7-nav-right>
          <f7-link popup-close>{{ $t('cerrar') }}</f7-link>
        </f7-nav-right>
      </f7-navbar>

      <f7-block strong class="aviso aviso-azul">
        <p>{{ $t('registrarVacaciones.explicacion') }}</p>
      </f7-block>

      <!-- La persona. Es lo primero: sin ella no hay saldo que calcular ni mes que comprobar. -->
      <f7-block-title>{{ $t('registrarVacaciones.persona') }}</f7-block-title>
      <f7-block strong>
        <template v-if="estadoPlantilla === 'error'">
          <p class="texto-error">{{ $t('registrarVacaciones.errorPlantilla') }}</p>
          <f7-link @click="cargarPlantilla">{{ $t('registrarVacaciones.reintentar') }}</f7-link>
        </template>
        <p v-else-if="estadoPlantilla === 'cargando'" class="apagado">
          {{ $t('registrarVacaciones.cargandoPlantilla') }}
        </p>
        <template v-else>
          <!-- **Se busca escribiendo**: regla permanente de dirección del 18/10/2026 para todo
               selector de personas de la ERP. Con una plantilla entera delante, encontrar a alguien
               desplegando la lista es ir leyendo nombre a nombre. Cómo se busca lo decide
               `buscarPersonas`, común para los selectores que vengan después. -->
          <input
            type="text"
            class="buscador-persona"
            :value="busquedaPersona"
            :placeholder="$t('registrarVacaciones.buscarPersona')"
            @input="busquedaPersona = $event.target.value"
          />
          <select class="selector-persona" :value="username" @change="elegirPersona($event)">
            <option value="">{{ $t('registrarVacaciones.elegirPersona') }}</option>
            <option v-for="p in plantillaQueSeOfrece" :key="p.username" :value="p.username">
              {{ rotuloPersona(p) }}
            </option>
          </select>
          <!-- Y si no coincide nadie se dice, con lo que se ha escrito dentro: un desplegable que
               se queda con una sola línea y sin explicación se lee como una plantilla vacía. -->
          <p v-if="busquedaSinNadie" class="apagado sin-coincidencias">
            {{ $t('registrarVacaciones.sinCoincidencias', { texto: busquedaPersona.trim() }) }}
            <f7-link @click="limpiarBusqueda">{{ $t('registrarVacaciones.quitarBusqueda') }}</f7-link>
          </p>
        </template>
      </f7-block>

      <!-- Uno o varios tramos. Aquí SÍ se admiten fechas pasadas: es la única excepción a la regla
           de fechas futuras, y es justo para lo que existe esta ventana. -->
      <f7-block-title>{{ $t('registrarVacaciones.tramos') }}</f7-block-title>
      <f7-block strong class="tramos">
        <div v-for="(tramo, i) in tramos" :key="i" class="tramo-fila">
          <label class="campo">
            <span class="campo-label">{{ $t('misVacaciones.desde') }}</span>
            <input type="date" :value="tramo.desde" @change="fijar(i, 'desde', $event)" />
          </label>
          <label class="campo">
            <span class="campo-label">{{ $t('misVacaciones.hasta') }}</span>
            <input type="date" :value="tramo.hasta" @change="fijar(i, 'hasta', $event)" />
          </label>
          <f7-link v-if="tramos.length > 1" class="quitar" @click="quitarTramo(i)">
            {{ $t('misVacaciones.quitarTramo') }}
          </f7-link>
        </div>
        <f7-link class="anadir" @click="anadirTramo">+ {{ $t('misVacaciones.anadirTramo') }}</f7-link>
      </f7-block>

      <f7-block v-if="tramosIncompletos && tramosCompletos.length" strong class="aviso aviso-error">
        <p>{{ $t('misVacaciones.tramoIncompleto') }}</p>
      </f7-block>

      <!-- El error del cálculo, SOLO si es el de la persona y las fechas que hay ahora delante
           (ver `errorVigente`): el de otra selección no se pinta aunque todavía esté guardado. -->
      <f7-block v-if="errorVigente" strong class="aviso aviso-error">
        <p>{{ errorVigente }}</p>
      </f7-block>

      <f7-block v-if="calculando" strong class="aviso">
        <p>{{ $t('misVacaciones.calculando') }}</p>
      </f7-block>

      <template v-if="previaVigente">
        <f7-block-title>
          {{ $t('misVacaciones.diasQueSeDescuentan') }}: {{ computo.diasQueDescuenta }}
        </f7-block-title>
        <f7-block strong class="chips">
          <span v-for="dia in computo.dias" :key="dia" class="chip">{{ corta(dia) }}</span>
          <span
            v-for="excluido in computo.diasExcluidos"
            :key="'x-' + excluido.fecha"
            class="chip chip-excluido"
            :title="excluido.detalle"
          >{{ corta(excluido.fecha) }} · {{ excluido.detalle }}</span>
          <p v-if="!computo.dias.length && !computo.diasExcluidos.length" class="apagado">
            {{ $t('misVacaciones.sinDias') }}
          </p>
        </f7-block>

        <!-- El saldo de antes y el de después. El de después lo calcula el servidor y puede ser
             negativo: aquí se pinta en rojo, que es lo que pide el punto 5. -->
        <f7-block strong class="saldos">
          <span>{{ $t('registrarVacaciones.saldoAhora') }}: <b>{{ saldoAhora }}</b></span>
          <span>
            {{ $t('registrarVacaciones.saldoDespues') }}:
            <b :class="{ 'saldo-negativo': saldoNegativo }">{{ previa.saldoDespues }}</b>
          </span>
        </f7-block>

        <!-- Saldo negativo: AVISO, no error. Se puede registrar igual —se está anotando algo que
             ya ocurrió—, pero hay que confirmarlo (JJCS, 21/09/2026). -->
        <f7-block v-if="saldoNegativo" strong class="aviso aviso-error">
          <p>{{ $t('registrarVacaciones.avisoSaldo', { persona: nombrePersona, saldo: previa.saldoDespues }) }}</p>
        </f7-block>

        <!-- Y si no llega el saldo pero no se queda en negativo, se dice igual: son días que no
             caben en el derecho de ESAS fechas, aunque el arrastre tape el número de hoy (D2). -->
        <f7-block v-else-if="diasQueFaltan" strong class="aviso aviso-naranja">
          <p>{{ $t('registrarVacaciones.avisoNoCaben', { faltan: diasQueFaltan }) }}</p>
        </f7-block>

        <f7-block v-if="mesesCerrados.length" strong class="aviso aviso-naranja">
          <p class="aviso-titulo">{{ $t('registrarVacaciones.avisoMesTitulo') }}</p>
          <p class="aviso-linea">{{ $t('registrarVacaciones.avisoMes', { meses: textoMeses }) }}</p>
        </f7-block>
      </template>

      <!-- El motivo es obligatorio: es lo único que explica por qué estas vacaciones no han
           pasado por el camino normal, y es lo que queda en rh_audit (I6). -->
      <f7-block-title>{{ $t('registrarVacaciones.motivo') }}</f7-block-title>
      <f7-block strong>
        <textarea
          class="comentario"
          rows="3"
          :value="motivo"
          :placeholder="$t('registrarVacaciones.motivoPlaceholder')"
          @input="motivo = $event.target.value"
        ></textarea>
      </f7-block>

      <f7-block strong class="pie">
        <span class="apagado">{{ $t('registrarVacaciones.pieConfirmada') }}</span>
        <span class="botones">
          <f7-button :text="$t('misVacaciones.volver')" class="display-flex" @click="$emit('cerrar')"></f7-button>
          <f7-button
            fill
            :text="$t('registrarVacaciones.registrar')"
            :disabled="!puedeRegistrar"
            class="display-flex"
            @click="registrar"
          ></f7-button>
        </span>
        <!-- **Qué falta para poder registrar, junto al botón** y no en un aviso al principio de la
             pantalla: §9, regla 2 de dirección del 05/10/2026. Sale de la misma lista que apaga el
             botón (`motivosParaNoRegistrar`), así que no se pueden desmentir. -->
        <p v-if="motivosParaNoRegistrar.length" class="falta-para-registrar">
          {{ $t('registrarVacaciones.noSePuedeRegistrar') }}
          <span v-for="(falta, i) in motivosParaNoRegistrar" :key="i" class="falta">{{ falta }}</span>
        </p>
      </f7-block>
    </f7-page>
  </f7-popup>
</template>
<style scoped>
.selector-persona,
.buscador-persona {
  width: 100%;
  box-sizing: border-box;
  border: 1px solid #c8c9cc;
  border-radius: 4px;
  padding: 6px 8px;
  font-size: 14px;
  background-color: #fff;
}
/* El buscador va encima del desplegable y pegado a él: son el mismo control. */
.buscador-persona { margin-bottom: 6px; }
.sin-coincidencias { margin: 4px 0 0; }

.tramos { padding: 8px 10px; }
.tramo-fila { display: flex; flex-wrap: wrap; align-items: flex-end; gap: 10px; margin-bottom: 8px; }
.campo { display: flex; flex-direction: column; flex: 1 1 140px; }
.campo-label { font-size: 11px; color: #64748b; }
.campo input {
  border: 1px solid #c8c9cc;
  border-radius: 4px;
  padding: 6px 8px;
  font-size: 14px;
  width: 100%;
  box-sizing: border-box;
}
.quitar { font-size: 12px; color: #d9534f; }
.anadir { font-size: 13px; }

.chips { display: flex; flex-wrap: wrap; gap: 6px; }
.chip {
  display: inline-block;
  border: 1px solid #c8c9cc;
  border-radius: 4px;
  padding: 2px 8px;
  font-size: 12px;
  background-color: #fff;
}
.chip-excluido { text-decoration: line-through; color: #8e8e93; background-color: #f4f5f7; }

.aviso { border-left: 4px solid #cfd4da; }
.aviso p { margin: 3px 0; font-size: 13px; }
.aviso-error { background-color: #fdecea; border-left-color: #d9534f; color: #8a1c13; }
.aviso-naranja { background-color: #fff4e5; border-left-color: #f0a000; color: #8a4b00; }
.aviso-azul { background-color: #eef6ff; border-left-color: #2196f3; color: #11508c; }
.aviso-titulo { font-weight: bold; }
.aviso-linea { font-size: 13px; }

.saldos { display: flex; flex-wrap: wrap; gap: 16px; font-size: 13px; color: #1a2744; }
/* El rojo del saldo negativo, el mismo que el resto de los errores de la pantalla. */
.saldo-negativo { color: #d9534f; }

.comentario {
  width: 100%;
  box-sizing: border-box;
  border: 1px solid #c8c9cc;
  border-radius: 4px;
  padding: 6px 8px;
  font-size: 14px;
  font-family: inherit;
}

.pie {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
  justify-content: space-between;
}
.botones { display: flex; gap: 8px; }
.apagado { font-size: 12px; color: #8e8e93; }
.texto-error { font-size: 13px; color: #8a1c13; margin: 0 0 4px 0; }
/* Mismo tamaño y mismo color que el aviso equivalente de `solicitar-vacaciones.vue`: es el mismo
   control y la misma regla, y dos tonos distintos para lo mismo se leen como dos cosas. */
.falta-para-registrar { flex: 1 1 100%; margin: 4px 0 0; font-size: 12px; color: #8a4b00; }
.falta { display: block; }
</style>
<script>
import RhSolicitudesVacaciones from "./../../js/db/RhSolicitudesVacaciones.js";
import mensajeDelServidor from "./../../js/utils/mensajeDelServidor.js";
import RhVacaciones from "./../../js/db/RhVacaciones.js";
import buscarPersonas from "./../../js/utils/buscarPersonas.js";
import carrilPeticiones from "./../../js/utils/carrilPeticiones.js";
import soloMandaLaDeDelante, { MOTIVO_DESTRUCCION } from "./../../js/utils/soloMandaLaDeDelante.js";

/**
 * «Registrar vacaciones en nombre de otra persona» (#70). Solo la abren ADMIN y DIRECTOR, y sirve
 * para las vacaciones ya disfrutadas o comunicadas por otra vía: nacen aprobadas, con motivo
 * obligatorio, y es el único sitio de la aplicación donde se admiten **fechas pasadas**.
 *
 * **Las cuentas y los dos avisos los hace el servidor**, igual que en el formulario de solicitud
 * (#58): los días que descuentan, en qué saldo queda la persona y qué meses de los cuadros de mando
 * están cerrados llegan de la vista previa. Aquí no se resta nada ni se mira ninguna ficha mensual,
 * porque entonces habría dos versiones de esas reglas.
 *
 * Lo que sí decide esta pantalla es el orden de las dos preguntas antes de guardar —primero el mes
 * cerrado y después el saldo— y que cancelar cualquiera de las dos **no escriba nada**. Las
 * confirmaciones viajan al servidor, que las vuelve a exigir: la ventana no es la garantía.
 */
export default {
  // Las tres formas de dejar de mandar —cerrar sesión, salir de la pantalla que lo contiene y
  // destruirse— acaban en `dejarDeMandar`, igual que en el formulario de solicitud. Sin esto esta
  // ventana no se enteraba de ninguna de las tres: la ruta es `keepAlive`, así que seguía montada
  // y abierta sobre el login o sobre la cuenta siguiente, y sus dos lecturas seguían siendo las
  // vigentes de sus carriles (revisión CODEX del PR #1737).
  mixins: [soloMandaLaDeDelante],
  props: {
    opened: { type: Boolean, default: false },
    /**
     * **El registro del «cargando» de la ventana que contiene este popup**, no uno propio.
     *
     * El preloader de Framework7 es un modal de la **aplicación** y no se cuenta: `show()` no hace
     * nada si ya hay uno y `hide()` quita el que haya, sea de quien sea. Con un vigilante propio
     * aquí serían dos que no se hablan, y el que terminara antes apagaría el de la recarga que
     * sigue en vuelo —exactamente lo que costó las rondas 4 y 11 del PR #1733—. Es la misma prop y
     * el mismo contrato que `solicitar-vacaciones.vue`.
     */
    reloj: { type: Object, required: true }
  },
  data: function() {
    return {
      plantilla: [],
      /** `'cargando'`, `'ok'` o `'error'`. Ver `cargarPlantilla`. */
      estadoPlantilla: 'cargando',
      /**
       * Lo escrito en el buscador del desplegable de personas. **No toca la selección**: filtrar la
       * lista no deselecciona a nadie ni tira la vista previa, que es de la persona elegida y no de
       * lo que se esté buscando (ver `plantillaQueSeOfrece`).
       */
      busquedaPersona: '',
      username: '',
      tramos: [this.tramoVacio()],
      previa: null,
      /** Para qué persona y fechas es la vista previa que hay en pantalla. Ver `previaVigente`. */
      previaDe: null,
      errorCalculo: null,
      /**
       * **De qué persona y fechas es ese error.** Lo mismo que `previaDe` es para `previa`, y por
       * el mismo motivo: lo que el cálculo escribe se pinta si y solo si casa con lo que hay
       * delante (ver `errorVigente`). Sin firma, el error había que borrarlo a mano en cada vía
       * que cambia la selección, y la vía que se olvidaba lo dejaba puesto.
       */
      errorCalculoDe: null,
      motivo: '',
      /**
       * Si hay un registro en marcha. Apaga el botón, así que se pinta y va en `data`. Lo suelta
       * `concluirRegistro`, por los dos caminos: un cerrojo que se queda puesto deja el botón mudo,
       * que es lo que prohíbe §9.
       */
      registrando: false
    };
  },
  created: function() {
    // Teclear una fecha dispara varios `change` y las respuestas no vuelven en orden: sin el
    // carril, el cálculo de un tramo a medio escribir puede llegar después y pintarse encima.
    // Mismo motivo y mismo utilitario que el formulario de solicitud.
    this.carril = carrilPeticiones();
    this.carrilPlantilla = carrilPeticiones();
    // Qué registro está en marcha, como testigo propio. NO es `registrando`: ese es lo que se
    // pinta, y esto es quién responde de soltarlo (ver `concluirRegistro`).
    this.registroEnCurso = null;
  },
  computed: {
    /** La plantilla que casa con lo que se ha escrito en el buscador (ver `buscarPersonas`). */
    plantillaEncontrada: function() {
      return buscarPersonas(this.plantilla, this.busquedaPersona);
    },
    /**
     * **Lo que el desplegable ofrece: lo encontrado, más la persona ya elegida.**
     *
     * La elegida se queda aunque la búsqueda de ahora no la encuentre, y es a propósito: un
     * `<select>` cuyo `value` no está entre sus `<option>` se pinta **en blanco**, así que al
     * escribir en el buscador la pantalla diría «no has elegido a nadie» mientras debajo enseña los
     * días, el saldo y los avisos de esa persona —dos partes de la misma pantalla diciendo lo
     * contrario, §9 del `CLAUDE.md` del orquestador—. Y deseleccionarla al buscar sería peor:
     * tiraría la vista previa que ya se ha calculado.
     */
    plantillaQueSeOfrece: function() {
      const elegida = this.personaElegida;
      if (!elegida || this.plantillaEncontrada.indexOf(elegida) !== -1) {
        return this.plantillaEncontrada;
      }
      return [elegida].concat(this.plantillaEncontrada);
    },
    /** La fila de la plantilla de la persona elegida, o nada. */
    personaElegida: function() {
      const self = this;
      if (!this.username) return null;
      return this.plantilla.filter(function(p) { return p.username === self.username; })[0] || null;
    },
    /** Se ha buscado algo y no coincide nadie. Lo dice la pantalla; ver la plantilla. */
    busquedaSinNadie: function() {
      return !!this.busquedaPersona.trim() && this.plantillaEncontrada.length === 0;
    },
    tramosCompletos: function() {
      return this.tramos.filter(function(t) { return t.desde && t.hasta; });
    },
    tramosIncompletos: function() {
      return this.tramos.some(function(t) { return !t.desde || !t.hasta; });
    },
    /** La firma de lo que hay en pantalla: la persona y las dos fechas de cada tramo. */
    firmaActual: function() {
      if (!this.username || this.tramosIncompletos || !this.tramosCompletos.length) return null;
      return this.firmaDe(this.username, this.tramos);
    },
    /**
     * **Si la vista previa que hay en pantalla es la de lo que hay en pantalla.** Es la única
     * condición que deja registrar, por lo mismo que en el formulario de solicitud: mientras no
     * haya llegado la respuesta de ESTA persona y ESTAS fechas, lo que se ve —los días, el saldo
     * que queda, los meses cerrados— es de otra selección, y guardar con eso delante enseñaría
     * unos avisos y confirmaría otros.
     */
    previaVigente: function() {
      return !!this.previa && !!this.previaDe && this.previaDe === this.firmaActual;
    },
    /**
     * **El error del cálculo que se puede decir**: el de la persona y las fechas de ahora, o
     * nada. Misma regla que `previaVigente` y por lo mismo —lo que se pinta es lo que casa con la
     * firma de lo que hay delante—, extendida al último estado del cálculo que no la tenía.
     *
     * Es lo que cierra de raíz una familia entera de fallos que venía saliendo ronda tras ronda:
     * `errorCalculo` era el único estado del cálculo **sin firma**, así que había que borrarlo a
     * mano en cada sitio que cambiara la selección, y el sitio que se olvidaba dejaba a la vista
     * el veredicto de unas fechas que ya no son las elegidas —mientras el pie pedía completar el
     * periodo: dos partes de la misma pantalla diciendo lo contrario, §9 del `CLAUDE.md` del
     * orquestador—. Pasó al corregir las fechas (ronda 1) y al añadir un periodo (ronda 3), y
     * cubre igual a la respuesta que llega tarde: escribirá su error con SU firma, que ya no es
     * la de ahora, así que no se pinta.
     */
    errorVigente: function() {
      if (!this.errorCalculo || this.errorCalculoDe === null) return null;
      return this.errorCalculoDe === this.firmaActual ? this.errorCalculo : null;
    },
    calculando: function() {
      return !!this.firmaActual && !this.errorVigente && !this.previaVigente;
    },
    computo: function() {
      return (this.previaVigente && this.previa.computo) || null;
    },
    saldoAhora: function() {
      const saldo = this.computo && this.computo.saldo;
      return saldo && saldo.saldo !== null && saldo.saldo !== undefined ? saldo.saldo : '—';
    },
    saldoNegativo: function() {
      return this.previaVigente && this.previa.saldoDespues < 0;
    },
    /** Días que no caben en el derecho de esas fechas, según el servidor (D2). */
    diasQueFaltan: function() {
      return this.computo && this.computo.diasQueFaltan ? this.computo.diasQueFaltan : 0;
    },
    mesesCerrados: function() {
      return (this.previaVigente && this.previa.mesesCerrados) || [];
    },
    /**
     * **«octubre de 2026 (OEA y OEB)»: cada mes una sola vez**, con sus dos cuadros de mando
     * dentro del paréntesis.
     *
     * El servidor devuelve **un aviso por ficha cerrada**, así que un mes con las dos cerradas
     * llegan dos veces y enumerarlos tal cual decía «octubre de 2026 (OEA), octubre de 2026
     * (OEB)»: dirección lo rechazó el 18/10/2026 al probarlo. Lo que cambia es cómo se lee, no lo
     * que se confirma —las confirmaciones siguen viajando **clave a clave**, una por ficha (ver
     * `registrar`), porque son dos objetivos distintos y el servidor las exige por separado—.
     *
     * Sale de aquí para el aviso amarillo y para la pregunta de antes de guardar, que es el mismo
     * texto en los dos sitios y tiene que decir lo mismo.
     */
    textoMeses: function() {
      const self = this;
      const grupos = [];
      this.mesesCerrados.forEach(function(m) {
        const clave = m.anio + '-' + m.mes;
        let grupo = grupos.filter(function(g) { return g.clave === clave; })[0];
        if (!grupo) {
          grupo = { clave: clave, mes: m.mes, anio: m.anio, cuadros: [] };
          grupos.push(grupo);
        }
        if (m.cuadro && grupo.cuadros.indexOf(m.cuadro) === -1) grupo.cuadros.push(m.cuadro);
      });
      return grupos.map(function(g) {
        return self.$t('registrarVacaciones.mesLargo_' + g.mes) + ' '
          + self.$t('registrarVacaciones.de') + ' ' + g.anio
          + (g.cuadros.length ? ' (' + self.enumerar(g.cuadros) + ')' : '');
      }).join(', ');
    },
    nombrePersona: function() {
      if (this.previaVigente && this.previa.nombre) return this.previa.nombre;
      const elegida = this.personaElegida;
      return (elegida && (elegida.nombre || elegida.username)) || this.username;
    },
    puedeRegistrar: function() {
      return this.motivosParaNoRegistrar.length === 0;
    },
    /**
     * **Qué falta para poder registrar, junto al botón.** Es la segunda regla de pantalla de
     * dirección del 05/10/2026 (§9 del `CLAUDE.md` del orquestador): un control que no se puede
     * usar dice por qué, quién lo desbloquea y dónde, y no se queda mudo. Aquí lo desbloquea quien
     * está delante —es lo que le falta rellenar—, así que lo que se dice es el paso siguiente.
     *
     * Es la **misma lista que decide si el botón se puede pulsar**, no una explicación aparte: así
     * no se pueden desmentir, y una condición nueva no puede apagar el botón sin escribir su
     * frase. Mismo patrón y mismas claves que `solicitar-vacaciones.vue#motivosParaNoEnviar`.
     */
    motivosParaNoRegistrar: function() {
      const faltas = [];
      // 0 · Ya se está registrando. Va primero y corta: mientras el servidor contesta no falta
      // nada más, y lo único que hay que decir es que espere. Sin esto el botón seguía vivo
      // durante todo el POST y un doble toque dejaba dos solicitudes idénticas —y el saldo
      // descontado dos veces—; el adaptador corta la segunda, pero el botón tiene que decirlo.
      if (this.registrando) {
        return [this.$t('registrarVacaciones.faltaEsperarElRegistro')];
      }
      if (!this.username) {
        faltas.push(this.$t('registrarVacaciones.faltaElegirPersona'));
      }
      // Las fechas y su cálculo, encadenados porque son estados del mismo paso: sin fechas no hay
      // periodo a medias, y sin periodo completo no hay cálculo que esperar.
      if (!this.tramosCompletos.length) {
        faltas.push(this.$t('misVacaciones.faltaElegirFechas'));
      } else if (this.tramosIncompletos) {
        faltas.push(this.$t('misVacaciones.faltaCompletarPeriodo'));
      } else if (this.errorVigente) {
        faltas.push(this.$t('misVacaciones.faltaCorregirFechas'));
      } else if (!this.previaVigente) {
        // Lo que se guarda es exactamente lo que se ha previsualizado, o no se guarda.
        faltas.push(this.$t('misVacaciones.faltaEsperarElCalculo'));
      } else if (this.computo.diasQueDescuenta === 0) {
        faltas.push(this.$t('misVacaciones.faltanDiasQueDescontar'));
      }
      if (!this.motivo.trim()) {
        faltas.push(this.$t('misVacaciones.faltaElMotivo'));
      }
      return faltas;
    }
  },
  methods: {
    tramoVacio: function() {
      return { desde: null, hasta: null };
    },
    /** «OEA y OEB», «OEA, OEB y OEC»: una enumeración que se lee, no una lista con comas. */
    enumerar: function(cosas) {
      if (cosas.length <= 1) return cosas.join('');
      return cosas.slice(0, -1).join(', ') + ' ' + this.$t('registrarVacaciones.y') + ' '
        + cosas[cosas.length - 1];
    },
    /**
     * Cómo se nombra a una persona en el desplegable: «Laura Martín · LMARTIN». **El código se ve**
     * porque también se puede buscar por él, y un atajo que no se enseña en ningún sitio no lo usa
     * nadie. Sin nombre en la ficha, el código ya es el rótulo y no se repite.
     */
    rotuloPersona: function(persona) {
      const nombre = persona.nombre || persona.username;
      return nombre === persona.username ? nombre : nombre + ' · ' + persona.username;
    },
    /** Vuelve a ofrecer la plantilla entera. La persona elegida no se toca. */
    limpiarBusqueda: function() {
      this.busquedaPersona = '';
    },
    /**
     * **Esta ventana deja de mandar sobre lo que tiene en vuelo**, por cualquiera de las tres
     * salidas que enumera `soloMandaLaDeDelante`. Mismo contrato y mismos motivos que
     * `solicitar-vacaciones.vue#dejarDeMandar`, donde está el porqué largo; aquí se repite la
     * forma, no el razonamiento.
     *
     * Las **dos lecturas** se tiran siempre: la vista previa y la plantilla. El popup no se
     * destruye al cerrarlo ni al cerrar sesión, así que sin esto la respuesta que estuviera en
     * vuelo seguía siendo la vigente de su carril y podía poblar el desplegable con la plantilla
     * que leyó la cuenta anterior, o escribir el saldo de una persona que quien entra después no
     * tiene por qué ver.
     *
     * El **registro** solo se concluye al destruirse, y por lo mismo que el envío del formulario:
     * lo que hay que soltar ahí es el reloj —un modal de la **aplicación**, que un dueño que ya no
     * existe dejaría puesto encima de la pantalla siguiente—. Al salir o al cerrar sesión el
     * registro de relojes es de la ventana y ella lo suelta entero; y `registrando` no se toca por
     * ninguna de las tres: lo suelta el registro que lo cogió (`concluirRegistro`) o `abrir()` al
     * reabrir, porque encenderlo con el POST vivo deja un botón que el adaptador rechaza como
     * repetido, o sea un botón que al pulsarlo no hace nada (§9).
     */
    dejarDeMandar: function(motivo) {
      this.olvidarCalculo();
      this.carrilPlantilla.invalidar();
      if (motivo === MOTIVO_DESTRUCCION && this.registroEnCurso) {
        this.concluirRegistro(this.registroEnCurso);
      }
    },
    /**
     * **La vista previa que hay en pantalla deja de valer**: se tira el carril y se tira lo que
     * ese carril había escrito. Las tres cosas juntas y en un solo sitio, igual que
     * `solicitar-vacaciones.vue#olvidarCalculo` y por el mismo motivo, que allí costó una ronda de
     * revisión (PR #1733, ronda 5):
     *
     *  - limpiar el estado **sin invalidar el carril** dejaba a la petición en vuelo siendo la
     *    vigente, así que al fallar volvía a escribir en `errorCalculo` el error de unas fechas que
     *    ya no están seleccionadas, y el mensaje se quedaba puesto sobre el formulario incompleto
     *    —que al mismo tiempo pide «completa el periodo»: dos partes de la misma pantalla diciendo
     *    lo contrario, que es lo que prohíbe §9 del `CLAUDE.md` del orquestador—;
     *  - invalidar el carril **sin limpiar** deja a la vista el saldo y los avisos de otra persona
     *    o de otras fechas.
     *
     * Las cuatro cosas tienen firma y sus dos `*Vigente` ya no las pintan en cuanto deja de casar,
     * pero se limpian igual: el carril que las escribió ya no manda, así que tampoco su respuesta.
     */
    olvidarCalculo: function() {
      this.carril.invalidar();
      this.previa = null;
      this.previaDe = null;
      this.errorCalculo = null;
      this.errorCalculoDe = null;
    },
    /**
     * Al abrir se vuelve a empezar: el popup vive dentro de la pantalla y no se destruye al
     * cerrarlo, así que sin esto el segundo registro saldría con la persona, las fechas y el
     * motivo del primero ya puestos —y el motivo de otra persona es exactamente lo que no puede
     * quedar en rh_audit—.
     */
    abrir: function() {
      // El cálculo y su carril se olvidan por el mismo sitio que cuando se borra una fecha (ver
      // `olvidarCalculo`): con el estado escrito a mano aquí, cualquier campo nuevo habría que
      // acordarse de limpiarlo dos veces.
      this.olvidarCalculo();
      this.carrilPlantilla.invalidar();
      // La ruta lleva `keepAlive`, así que este popup no se recrea: un cerrojo que dejó puesto el
      // registro de otra sesión seguiría apagando el botón para quien entre después.
      this.soltarRegistroDeOtraSesion();
      this.username = '';
      // Lo buscado se va con la persona: dejarlo puesto abriría el segundo registro con la
      // plantilla recortada por el nombre que se buscó para el primero.
      this.busquedaPersona = '';
      this.tramos = [this.tramoVacio()];
      this.motivo = '';
      this.cargarPlantilla();
    },
    /**
     * La plantilla, del Mapa de aprobación (#52), que es el listado que ya existe de toda la
     * gente con ficha de vacaciones. **Un fallo al leerla no se enseña como «no hay nadie»**: se
     * dice y se puede reintentar, igual que las normas en el formulario de solicitud; un
     * desplegable vacío parecería que la plantilla no está cargada en el sistema.
     *
     * Se dejan fuera quienes no gestionan sus vacaciones en su usuario (D-USR): el servidor
     * redirigiría a su cuenta gestora, así que ofrecer las dos sería ofrecer dos veces a la misma
     * persona.
     */
    cargarPlantilla: function() {
      const self = this;
      self.estadoPlantilla = 'cargando';
      self.carrilPlantilla.lanzar(RhVacaciones.mapa(false), {
        exito: function(res) {
          const filas = (res && res.filas) || [];
          self.plantilla = filas
            .filter(function(f) { return f.gestionaVacaciones; })
            .sort(function(a, b) {
              return String(a.nombre || a.username).localeCompare(String(b.nombre || b.username));
            });
          self.estadoPlantilla = 'ok';
        },
        limpiar: function() {
          self.plantilla = [];
          self.estadoPlantilla = 'error';
        }
      });
    },
    elegirPersona: function(evento) {
      this.username = evento.target.value || '';
      // El saldo y los meses cerrados son de la persona: lo calculado para otra no vale ni para
      // pintarlo mientras llega lo nuevo, y su carril tampoco manda ya.
      this.olvidarCalculo();
      this.calcular();
    },
    fijar: function(indice, campo, evento) {
      const tramo = Object.assign({}, this.tramos[indice]);
      tramo[campo] = evento.target.value || null;
      this.$set(this.tramos, indice, tramo);
      this.calcular();
    },
    /**
     * Añadir un periodo **no lanza cálculo**: el tramo nace vacío, así que no hay nada que
     * calcular hasta que se rellene. Y tampoco tiene que limpiar nada: lo que hubiera calculado
     * —la vista previa y el error— deja de pintarse solo, porque la firma de la pantalla ha
     * cambiado (ver `errorVigente` y `previaVigente`).
     */
    anadirTramo: function() {
      this.tramos.push(this.tramoVacio());
    },
    quitarTramo: function(indice) {
      this.tramos.splice(indice, 1);
      this.calcular();
    },
    firmaDe: function(username, tramos) {
      return JSON.stringify([username, tramos.map(function(t) { return [t.desde, t.hasta]; })]);
    },
    calcular: function() {
      const self = this;
      const firma = self.firmaActual;
      if (!firma) {
        self.olvidarCalculo();
        return;
      }
      // **El error de antes se va al lanzar, no al contestar**, igual que en
      // `solicitar-vacaciones.vue#calcular` y por lo mismo: es el veredicto de unas fechas —o de
      // una persona— que ya no son las elegidas. Dejándolo hasta el éxito, `motivosParaNoRegistrar`
      // seguía diciendo «corrige las fechas» de las anteriores mientras el servidor calculaba las
      // nuevas, y además sin el «calculando», que depende de que no haya error: con una conexión
      // lenta, la pantalla afirmando que lo que acabas de arreglar sigue mal (§9 del `CLAUDE.md`
      // del orquestador: dos partes de la misma pantalla no pueden decir lo contrario).
      //
      // Borrarlo aquí es para que no quede guardado de una selección a otra; lo que GARANTIZA que
      // no se pinte es su firma (`errorVigente`), que no depende de que nadie se acuerde.
      self.errorCalculo = null;
      self.errorCalculoDe = null;
      self.carril.lanzar(
        RhSolicitudesVacaciones.vistaPreviaRegistro(self.username, self.tramosCompletos), {
          exito: function(res) {
            // Ya se limpió al lanzar, y nadie puede haberlo puesto mientras: `fallo` solo corre
            // para la petición vigente y las de antes pasan por `descartada`.
            self.previa = res || null;
            self.previaDe = self.previa ? firma : null;
          },
          limpiar: function() {
            // Si falla, lo que queda en pantalla es el cálculo anterior: el saldo y los avisos de
            // otras fechas, o de otra persona.
            self.previa = null;
            self.previaDe = null;
          },
          fallo: function(e) {
            // El mensaje del servidor es el que explica qué pasa con ESAS fechas («no puede
            // mezclar días de dos años», «ya tiene vacaciones esos días»): se enseña tal cual, y
            // **diciendo de qué fechas es**, que es lo que impide enseñarlo sobre otras. Salvo el
            // de un fallo no previsto, que es texto de Java (#829, ver `mensajeDelServidor`).
            self.errorCalculo = mensajeDelServidor(e, self.$t('misVacaciones.errorCalculando'));
            self.errorCalculoDe = firma;
          }
        });
    },
    /**
     * Las dos preguntas antes de guardar, en cadena: primero el mes cerrado y después el saldo.
     * Cancelar cualquiera de las dos corta y **no escribe nada**, que es el criterio de
     * aceptación. Solo se pregunta lo que de verdad hace falta: sin mes cerrado no hay primera
     * pregunta, y con saldo de sobra no hay segunda.
     *
     * **Y aquí se nombra la acción**, antes del primer diálogo: con la sesión de ahora —su sello,
     * que lleva dentro la generación— y con la persona, las fechas, el motivo y las dos
     * confirmaciones **tal y como están en este momento**. De ahí en adelante nadie vuelve a mirar
     * la pantalla: lo que se guarda es lo que se preguntó.
     *
     * El porqué está en `RhSolicitudesVacaciones.accionDeEstaSesion`: estos dos «¿Continuar?» los
     * monta el marco de la aplicación por encima de la pantalla, así que `dejarDeMandar` no los
     * alcanza. Si la sesión se cae con uno abierto, lo que pulsa «Continuar» es otra persona.
     *
     * **Y se preguntan por el adaptador** (`preguntarEnEstaSesion`), no con `$f7.dialog.confirm` a
     * secas: ahí el diálogo queda apuntado a la acción, así que al cerrarse la sesión se cierra
     * también. Sin eso, el aviso del saldo se quedaba PINTADO —con el nombre de la persona y su
     * saldo en rojo— sobre el login o sobre la cuenta siguiente (revisión CODEX del PR #1737).
     *
     * **Y el cerrojo se coge aquí, al nombrar la acción, no en `guardar`.** Entre la pulsación y
     * el POST hay dos diálogos, así que con el cerrojo puesto solo en `guardar` el botón seguía
     * vivo todo ese rato y dos pulsaciones abrían **dos cadenas de diálogos** con los mismos
     * datos. Que el adaptador corte las escrituras simultáneas no basta: si la primera termina
     * antes de que se conteste la segunda cadena, su clave ya está suelta y la segunda guarda otra
     * vez —la misma persona, las mismas fechas y el saldo descontado dos veces— (revisión CODEX
     * del PR #1737, r4303373381). El testigo del registro nace también aquí y lo acompaña hasta el
     * final: lo suelta `concluirRegistro`, por el camino bueno, por el malo y por el de cancelar.
     */
    registrar: function() {
      const self = this;
      if (self.registrando) return;
      const accion = RhSolicitudesVacaciones.accionDeEstaSesion({
        username: self.username,
        tramos: self.tramosCompletos,
        motivo: self.motivo.trim(),
        // Las dos confirmaciones, atadas a la foto que se está enseñando: los meses por su clave y
        // el saldo por su número. El servidor las vuelve a exigir sobre lo que haya al guardar.
        mesesCerradosConfirmados: self.mesesCerrados.map(function(m) { return m.clave; }),
        saldoNegativoConfirmado: self.saldoNegativo ? self.previa.saldoDespues : null,
        // Para el aviso del final, que nombra a la persona: resolverlo después sería leerlo de una
        // pantalla que puede haberse quedado con otra selección.
        nombre: self.nombrePersona
      });
      // El reloj todavía no: es de la petición, y aquí no hay ninguna. Lo coge `guardar` sobre
      // este mismo testigo, que es lo que permite soltar las dos cosas en un solo sitio.
      const registro = { sesion: accion.sesion, reloj: null };
      self.registroEnCurso = registro;
      self.registrando = true;
      const abandonar = function() { self.concluirRegistro(registro); };
      if (self.mesesCerrados.length) {
        RhSolicitudesVacaciones.preguntarEnEstaSesion(self.$f7, accion,
          self.$t('registrarVacaciones.confirmaMes', { meses: self.textoMeses }),
          self.$t('registrarVacaciones.titulo'),
          function() { self.preguntarSaldo(accion, registro); },
          abandonar
        );
        return;
      }
      self.preguntarSaldo(accion, registro);
    },
    /**
     * La segunda pregunta. **Rechaza la acción si la sesión ya no es la que la empezó**, igual que
     * `guardar`: comprobarlo solo al final dejaría pasar lo que se decidiera entre un diálogo y el
     * siguiente, que es justo el hueco entre estos dos. Lo comprueba `preguntarEnEstaSesion` antes
     * de abrir nada, y se repite aquí para el camino que se salta el diálogo.
     */
    preguntarSaldo: function(accion, registro) {
      const self = this;
      if (!RhSolicitudesVacaciones.sigueSiendoDeEstaSesion(accion)) {
        self.concluirRegistro(registro);
        return;
      }
      if (accion.datos.saldoNegativoConfirmado !== null) {
        RhSolicitudesVacaciones.preguntarEnEstaSesion(self.$f7, accion,
          self.$t('registrarVacaciones.confirmaSaldo', {
            persona: accion.datos.nombre, saldo: accion.datos.saldoNegativoConfirmado
          }),
          self.$t('registrarVacaciones.titulo'),
          function() { self.guardar(accion, registro); },
          function() { self.concluirRegistro(registro); }
        );
        return;
      }
      self.guardar(accion, registro);
    },
    /**
     * Guarda el registro. Mismo esqueleto que el envío de una solicitud
     * (`solicitar-vacaciones.vue#enviar`) y por los mismos motivos, que están escritos allí: un
     * cerrojo propio para el botón y el reloj de la ventana con testigo, y los dos se sueltan en un
     * solo sitio (`concluirRegistro`). La diferencia con el envío es que aquí el cerrojo ya viene
     * cogido: el envío sale al pulsar y este, dos diálogos después, así que lo coge `registrar`.
     *
     * Las confirmaciones se mandan según lo que se haya preguntado de verdad, y **diciendo sobre
     * qué**: los meses por su clave y el saldo por su número. Un «sí» a secas valía también para un
     * mes que se cerrara después del aviso, o para un saldo peor del que se aceptó, y el servidor no
     * podía distinguirlo (revisión CODEX del PR #1984).
     *
     * **Es el punto común previo al POST, así que aquí se vuelve a preguntar si la acción sigue
     * siendo de esta sesión** —y el sello **no se renueva**: el que vale es el de cuando se empezó,
     * antes del primer diálogo—. Lo que se manda son los datos de `accion`, no lo que haya en
     * pantalla: con la sesión caída y otra persona delante, la pantalla puede tener otra cosa y lo
     * que se guardaría sería una mezcla de las dos (revisión CODEX del PR #1737, ronda 8).
     */
    guardar: function(accion, registro) {
      const self = this;
      const app = self.$f7;
      if (!RhSolicitudesVacaciones.sigueSiendoDeEstaSesion(accion)) {
        self.concluirRegistro(registro);
        return;
      }
      // El cerrojo ya está cogido desde `registrar`; lo que falta es el reloj, que es de esta
      // petición y no de la cadena de diálogos.
      registro.reloj = self.reloj.coger('registrar');
      RhSolicitudesVacaciones.registrarEnNombreDe({
        username: accion.datos.username,
        tramos: accion.datos.tramos,
        motivo: accion.datos.motivo,
        mesesCerradosConfirmados: accion.datos.mesesCerradosConfirmados,
        saldoNegativoConfirmado: accion.datos.saldoNegativoConfirmado
      })
        .then(function(solicitud) {
          self.concluirRegistro(registro);
          app.toast.create({
            text: self.$t('registrarVacaciones.registrada', {
              persona: accion.datos.nombre, codigo: (solicitud && solicitud.codigo) || ''
            }),
            position: 'center',
            closeTimeout: 3500
          }).open();
          // Lo último: quien escuche esto cierra el popup y recarga el resumen, y el reloj de esa
          // recarga es suyo.
          self.$emit('registrada', solicitud);
        })
        .catch(function(e) {
          self.concluirRegistro(registro);
          // Un intento repetido que haya cortado el adaptador, o una respuesta de otra sesión, no
          // tienen nada que decirle a quien está delante: el aviso era para quien registró. Y
          // tampoco se vuelve a calcular: ahí el POST de verdad puede seguir en vuelo.
          if (RhSolicitudesVacaciones.sinNadaQueDecir(e)) return;
          // **Lo que se mandó ya no vale, así que se vuelve a calcular antes de avisar.** El
          // servidor rechaza a propósito las confirmaciones atadas a una foto que ha cambiado
          // —otro mes cerrado, otro saldo—, y conservando la vista previa anterior el botón se
          // encendía otra vez y cada reintento mandaba exactamente las mismas confirmaciones
          // obsoletas: un botón que al pulsarlo no puede funcionar nunca, que es lo que prohíbe §9
          // del `CLAUDE.md` del orquestador. Al recalcular llegan los meses cerrados y el saldo de
          // ahora, y los dos «¿Continuar?» se preguntan sobre ellos (revisión CODEX del PR #1737,
          // ronda 7).
          self.olvidarCalculo();
          self.calcular();
          app.dialog.alert(mensajeDelServidor(e, self.$t('registrarVacaciones.errorRegistrando')));
        });
    },
    /**
     * **Un registro termina en un solo sitio**, por los dos caminos, y suelta dos cosas con dueños
     * distintos: el cerrojo del botón, que es de este registro y lo suelta él sin mirar la sesión,
     * y el reloj, que solo se suelta si el testigo sigue siendo el dueño. El porqué largo de esa
     * diferencia está en `solicitar-vacaciones.vue#concluirEnvio`; se repite la forma, no el
     * razonamiento.
     */
    concluirRegistro: function(registro) {
      this.reloj.soltar(registro.reloj);
      if (this.registroEnCurso !== registro) return;
      this.registroEnCurso = null;
      this.registrando = false;
    },
    /**
     * El cerrojo que dejó un registro de otra sesión. Lo llama `abrir()`: si ese POST nunca
     * contesta no hay quien suelte el cerrojo, y quien entre después se encuentra el botón apagado
     * esperando a un registro que no es suyo y que no va a terminar.
     */
    soltarRegistroDeOtraSesion: function() {
      const registro = this.registroEnCurso;
      if (!registro || registro.sesion === RhSolicitudesVacaciones.sesionActual()) return;
      this.concluirRegistro(registro);
    },
    /** «3 nov». Se compone del texto ISO sin pasar por `Date`, para que no se mueva de día. */
    corta: function(iso) {
      if (!iso) return '';
      const trozos = String(iso).substring(0, 10).split('-');
      if (trozos.length !== 3) return String(iso);
      return parseInt(trozos[2], 10) + ' ' + this.$t('misVacaciones.mes_' + parseInt(trozos[1], 10));
    }
  }
};
</script>
